export interface SearchDocument {
  searchText: string
  total: number
  items: Product[]
  facets: SearchFacet[]
  filters: SearchFilter
  limit: number
  suggestedTexts?: SearchSuggestion[]
  queryKey?: string
  similarResults?: { items: Product[] }
}

export type SearchRawDocument = Omit<SearchDocument, 'items'> & {
  items: RawProduct[]
}

export type UnparsedCategoryItem = {
  position: number
  id: number
  parent_id: number | null
  children_ids: number[]
  filterable_additional_fields: string[] | null
} & {
  [key: string]: string
}

export type RawFlagItem = FlagItem & { [x: string]: unknown }

export type FlagItem = {
  id: number
  image: string
  is_visible: boolean
  name: string
  position: number
  slug: string
  type: FlagType
}

export enum FlagType {
  Flag = '1',
  Offer = '2',
}

export type OfferItem = FlagItem & {
  images?: string[]
  description?: string
  expiration_date?: string
}

export type FlagsResponse = {
  facets: SearchFacet[]
  filters: SearchFilter
  searchText: string
  items: RawFlagItem[]
}

export type CategoryItem = {
  description?: string
  pageTitle: string
  children_ids?: number[]
  title: string
  slug: string
  parent_id?: number | null
  hierarchy?: string
  id: number
  position: number
  ogTitle?: string
  ogDescription?: string
  metaTitle?: string
  metaDescription?: string
  name: string
  count?: number
  bottomDescription?: string
  filterable_additional_fields: string[]
}

export type CategoryTree = CategoryItem & {
  children: CategoryTree[]
}

export type SearchCategory = {
  title: string
  count: number
  image?: string
  id?: string
}

export type SearchSuggestion = {
  suggestion: string
  facets: {
    [key: string]: SearchCategory[]
  }
}

export type StatsRange = {
  gte: string
  lte: string
}

export type QueryFilters = { [key: string]: string[] | string | StatsRange }

export type SearchFilter = {
  category_ids: number[]
  [key: string]: string[] | number[] | string | StatsRange
}

export interface ItemFieldValue {
  id: number
  value: string | null
  item_id: number
  default: boolean
  order: number
}

export interface ItemField {
  id: number
  name: string
  values: ItemFieldValue[]
}

export type RawProduct = {
  image: string
  is_from_inventory?: boolean
  old_price: number
  discount_percentage?: number
  id: number
  current_price: number
  in_stock: boolean
  manufacturer: string
  quote_slug: string
  temporarily_out_of_stock: boolean
  sku: string
  sticker_enabled: boolean
  sticker_img: string | null
} & { [x: string]: string | number | null | boolean | undefined }

export interface Product extends RawProduct {
  name: string
  category: string
  friendlyUrl?: string
  hash?: string
  category_slug?: string | null
  item_fields?: ItemField[]
}

export interface SearchFacet {
  key: string
  label: string
  items: SearchCategory[]
  type: FacetType
  min?: number
  max?: number
}

export enum CategoryIdEnum {
  Electronics = 2,
  Clothes = 2511,
  Footwear = 1932,
  Accessories = 1941,
  Cosmetics = 1984,
}

export enum PjCategoryIdEnum {
  Electronics = 684,
  Clothes = 2707,
  Footwear = 2429,
  Accessories = 2443,
  Cosmetics = 2471,
}

export enum ManufacturerEnum {
  Apple = 'Apple',
  Lenovo = 'Lenovo',
  Asus = 'Asus',
  Dell = 'Dell',
  MSI = 'MSI',
  JBL = 'JBL',
  Acer = 'Acer',
  HP = 'HP',
  UGG = 'UGG®',
  NewBalance = 'New Balance',
  Nike = 'Nike',
  Timberland = 'Timberland',
  PoloRalphLauren = 'Polo Ralph Lauren',
  BananaRepublic = 'Banana Republic',
  Columbia = 'COLUMBIA',
  Levis = "Levi's",
  COACH = 'COACH',
  MichaelKors = 'Michael Kors',
  DKNY = 'DKNY',
  TommyHilfiger = 'Tommy Hilfiger',
  CalvinKlein = 'Calvin Klein',
  Guess = 'Guess',
  USPoloAssn = 'U.S. Polo Assn.',
  Timeless = 'Timeless',
  VictoriasSecret = "Victoria's Secret",
  Crest = 'Crest',
  PredireParis = 'Predire Paris',
  Tarte = 'Tarte',
  Rhode = 'Rhode',
  Hempz = 'Hempz',
  KarlLagerfeldParis = 'Karl Lagerfeld Paris',
  KarlLagerfeld = 'Karl Lagerfeld',
  EsteeLauder = 'Estée Lauder',
}

export enum FacetKeyEnum {
  MANUFACTURER = 'manufacturer',
  CURRENT_PRICE = 'current_price',
  DISCOUNT_PERCENTAGE = 'discount_percentage',
  CATEGORY_ID = 'category_id',
  CATEGORY_IDS = 'category_ids',
}

export enum FacetType {
  Terms = 'terms',
  Stats = 'stats',
  Flags = 'flags',
}

export type SelectedFilterValue = string | number[]
export type SelectedFilter = { value: string[] | number[]; type: FacetType }

export enum SortDirection {
  PriceHighToLow,
  PriceLowToHigh,
  BestValue,
  BestSellers,
  NewArrivals,
  Relevance,
}

export enum SearchSortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

const relevanceSortDirection = { _relevance: SearchSortDirection.DESC }

export const SortDetails: {
  [key in SortDirection]: Record<string, SearchSortDirection>[]
} = {
  [SortDirection.PriceHighToLow]: [{ current_price: SearchSortDirection.DESC }, relevanceSortDirection],
  [SortDirection.PriceLowToHigh]: [{ current_price: SearchSortDirection.ASC }, relevanceSortDirection],
  [SortDirection.BestValue]: [
    { discount_percentage: SearchSortDirection.ASC },
    { current_price: SearchSortDirection.ASC },
    relevanceSortDirection,
  ],
  [SortDirection.BestSellers]: [{ paid_amount: SearchSortDirection.DESC }, relevanceSortDirection],
  [SortDirection.NewArrivals]: [{ arrived_at: SearchSortDirection.DESC }, relevanceSortDirection],
  [SortDirection.Relevance]: [
    relevanceSortDirection,
    { paid_amount: SearchSortDirection.DESC },
    { arrived_at: SearchSortDirection.DESC },
  ],
}

// Filter values already contain spaces, ampersands, dashes.
// Using double plus, in case it ends up in the value.
export const urlStatsDelimiter = '++'
